import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs';
import {instagram_redirect_uri, youtube_redirect_uri, youtube_redirect_uri_auth_only} from '../../store/constants';
import { MdDone } from "react-icons/md";
import { FaRobot } from "react-icons/fa";
import { MdOutlineBackHand } from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { setYoutubeAccountAccessType } from '../../store/publicStore/actions/utilsActions/utilsActions';
import { storeYoutubeAuth } from '../../store/BusinessStore/actions/InfluencerChannelActions/storeYoutubeAuthActions';
import { getYoutubeChannelList } from '../../store/BusinessStore/actions/InfluencerChannelActions/getYoutubeChannelListActions';
import { useToast } from '../../hooks/useToast';
import { getUserInfo } from '../../store/authStore/actions/authActions/getUserInfoActions';

/*
  A lightweight page that lets an Influencer decide whether PaidPromo will publish content
  automatically or they will upload it manually. Once the user chooses and hits
  "Continue to Connect" we immediately kick-off the Google OAuth flow – the
  same flow previously triggered directly from the Connect button.
*/

// Choose the appropriate Google OAuth Client ID based on current origin
const getGoogleClientId = () => {
  const origin = (typeof window !== 'undefined') ? window.location.origin : '';
  if (origin.includes('localhost')) {
    return '647395777111-ihuj3klciv80l73qr0hv8nmne2gud7mv.apps.googleusercontent.com'; // Dev
  }
  if (origin.includes('dev.paidpromo.ai')) {
    return '647395777111-ihuj3klciv80l73qr0hv8nmne2gud7mv.apps.googleusercontent.com'; // Dev
  }
  if (origin.includes('uat.paidpromo.ai')) {
    return '730502333817-jbcu1vvf134j5ptjp69jbq617cv84fbi.apps.googleusercontent.com'; // UAT
  }
  // default to UAT/Production
  return '97084085814-k0cgqe35u6gnv0qfch5grlrtcevgif0f.apps.googleusercontent.com'; // Prod
};
const CLIENT_ID = getGoogleClientId();

const AutomatedScopes = [
  'https://www.googleapis.com/auth/youtube.upload',
  'https://www.googleapis.com/auth/youtube.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
];

const ManualScopes = [
  'https://www.googleapis.com/auth/youtube.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
];

const AutomatedCheckList = [
  'Influencer grants Google authentication/consent to PaidPromo.',
  'Influencer only needs to submit content to the PaidPromo platform.',
  'PaidPromo will handle scheduling and uploading the video to their connected YouTube channel on their behalf.',
];

const ManualCheckList = [
  'Influencer grants limited Google access to PaidPromo.',
  'PaidPromo can only view channel details, video metrics, and statistics.',
  'Influencer must manually upload videos to their YouTube channel.',
  'After uploading, influencer submits the YouTube Video ID back to PaidPromo.',
  'PaidPromo will validate the submission and mark campaign participation as PUBLISHED after verification.'
];

// Instagram (via Facebook) OAuth configuration
const IG_APP_ID = '2231893297295855';
// Must be whitelisted in Meta App Dashboard; redirect back to this page so we can parse the token
const IG_REDIRECT_URI = typeof window !== 'undefined' ? `${window.location.origin}/influencer/choose-access-type` : 'http://localhost:3000/influencer/choose-access-type';
const IG_SCOPES_ARR_AUTOMATED = [
  // 'pages_show_list',
  // 'instagram_basic',
  // 'instagram_manage_insights',
  // 'instagram_content_publish',
  'instagram_business_basic',
  'instagram_business_content_publish',
  'instagram_business_manage_insights',

  // 'business_management',
  //   'pages_read_engagement'
];

const IG_SCOPES_ARR_MANUAL = [
  // 'pages_show_list',
  // 'instagram_basic',
  // 'instagram_manage_insights',
  'instagram_business_basic',
  'instagram_business_manage_insights',
  // 'instagram_business_manage_comments',
  // 'instagram_business_manage_messages',
  // 'instagram_content_publish',
  // 'whatsapp_business_management',
  //  'business_management',
  // 'pages_read_engagement'

];

const IG_SCOPES_AUTOMATED = IG_SCOPES_ARR_AUTOMATED.join(',');
const IG_SCOPES_MANUAL = IG_SCOPES_ARR_MANUAL.join(',');

function ChooseAccessType() {
  const { state } = useLocation();
  const dispatch = useDispatch();
  const Toast = useToast();
  // Determine desired platform from navigation state or query param
  // Determine desired platform from navigation state or query param
  const platformFromQuery = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('platform') : null;
  const platformLower = (state?.platform || platformFromQuery || '').toString().toLowerCase();
  const youtubeAccountAccessType = useSelector((state) => state.utilReducers.youtubeAccountAccessType);

  const getUserInfoReducerData = useSelector(
    (state) => state.getUserInfoReducer.data
  );

  // Ensure we have up-to-date user info so we can decide correct redirect target
  useEffect(() => {
    dispatch(getUserInfo());
  }, []);

  const [accessType, setAccessType] = useState('AUTOMATED');
  const [igAccessType, setIgAccessType] = useState('AUTOMATED');
  const [igAuthInfo, setIgAuthInfo] = useState(null);

  const breadcrumbData = [
    { label: 'Home', href: '/' },
    { label: 'My Campaigns', href: '/influencer/campaigns' },
    { label: 'Choose Your Access Type', href: '#' },
  ];

  const handleContinue = () => {
    dispatch(setYoutubeAccountAccessType(accessType === 'AUTOMATED' ? 'FULL_ACCESS' : 'READ_ONLY'));
    const scopes = accessType === 'AUTOMATED' ? AutomatedScopes : ManualScopes;
    const scopeParam = encodeURIComponent(scopes.join(' '));
    // Decide correct redirect: if user already has a YouTube channel connected, use auth-only
    const hasYouTubeChannel = Boolean(getUserInfoReducerData?.channelAccessType);
    const isYouTubeContext = (!platformLower || platformLower === 'youtube');
    const chosenRedirect = state?.redirectUri
      || (isYouTubeContext && hasYouTubeChannel ? youtube_redirect_uri_auth_only : youtube_redirect_uri);
    const redirectParam = encodeURIComponent(chosenRedirect);
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&redirect_uri=${redirectParam}&scope=${scopeParam}&response_type=code&access_type=offline&prompt=consent&include_granted_scopes=true`;

    console.log("authUrl", authUrl);

    window.location.href = authUrl;
  };

  const handleInstagramConnect = () => {
    dispatch(
      setYoutubeAccountAccessType(
        accessType === "AUTOMATED" ? "FULL_ACCESS" : "READ_ONLY"
      )
    );

    const scopes =
        accessType === "AUTOMATED"
          ? IG_SCOPES_ARR_AUTOMATED.join(",")
          : IG_SCOPES_ARR_MANUAL.join(",")


    // const encodedParams = encodeURIComponent(paramsJson);
    const scopeParam = encodeURIComponent(scopes);

    console.log("scopeParam:::::::::", scopeParam)

    // const authUrl = `https://instagram.com/consent/?flow=ig_biz_login_oauth&params_json=${encodedParams}&source=oauth_permissions_page_www`;
    const authUrl = `https://www.instagram.com/oauth/authorize?client_id=759190760186640&redirect_uri=https%3A%2F%2Fdev.paidpromo.ai%2Finfluencer%2Fchoose-access-type&response_type=code&scope=${scopeParam}&force_reauth=`

    console.log("authurllllllllll", authUrl)

    window.location.href = authUrl;
  };


  console.log("igAccessType", igAccessType);
  console.log("accessType", accessType);
  console.log("Platform", platformLower);


  useEffect(() => {
    if (platformLower === 'youtube') {
      if (getUserInfoReducerData?.channelAccessType === 'FULL_ACCESS') {
        setAccessType('AUTOMATED');
      } else {
        setAccessType('MANUAL');
      }
    }
    if (platformLower === 'instagram') {
      if (getUserInfoReducerData?.channelAccessTypeInstagram === 'FULL_ACCESS') {
        setAccessType('AUTOMATED');
      } else {
        setAccessType('MANUAL');
      }
    }
  }, [getUserInfoReducerData]);

  // Parse IG tokens if redirected back with fragment
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Instagram code can be returned either as a query param (?code=...) or in the hash.
      const searchParams = new URLSearchParams(window.location.search || '');
      const hashParams = window.location.hash
        ? new URLSearchParams(window.location.hash.substring(1))
        : null;

      const codeFromQuery = searchParams.get('code');
      const codeFromHash = hashParams ? hashParams.get('code') : null;
      const instaCode = codeFromQuery || codeFromHash;

      if (instaCode) {
        const info = {
          access_token: hashParams ? hashParams.get('access_token') : null,
          long_lived_token: hashParams ? (hashParams.get('long_lived_token') || null) : null,
          expires_in: hashParams ? (hashParams.get('expires_in') || null) : null,
        };

        console.log("InstaCode", instaCode)

        console.log("info", info.access_token)
        console.log("info", info.long_lived_token)
        console.log("info", info.expires_in)
        console.log("accessType::::::::::::::::::::::::", accessType)
        console.log("youtubeAccountAccessType::::::::::::::::::::::::", youtubeAccountAccessType)
        setIgAuthInfo(info);
        dispatch(storeYoutubeAuth({
          "tokenExpiredSecond": "",
          "shortLivedToken": "",
          "longLivedToken": "",
          "channelAccessType": youtubeAccountAccessType,
          "platform": "INSTAGRAM",
          "googleCode": instaCode ? instaCode : '',
          "redirectUri": instagram_redirect_uri,
        }, (status, response, message) => {
          // window.close()
          if (status === 200) {
            dispatch(getUserInfo((status, response, message) => {
              if (status === 200) {
                window.close()
              } else {
                Toast(message, 'error');
              }
            }))
          } else {
            Toast(message, 'error');
          }
        }));
        // Optional: clear hash to keep URL clean
        // window.location.hash = '';
      }
    }
  }, []);

  return (
    <div className="w-full flex flex-col bg-[#F2F3F7] min-h-screen">
      <Breadcrumbs breadcrumbs={breadcrumbData} />

      <div className="p-[4rem] flex-1 overflow-y-auto">
        <h2 className="text-[2rem] font-[600] text-[#111729] mb-2">Choose Your Access Type</h2>
        <p className="text-[1.6rem] text-gray-600 mb-6">
          Select how you’d like to manage your content publishing with PaidPromo
        </p>

        {/* Access type cards */}
        <div className="space-y-6">
          {/* Automated */}
          <div
            onClick={() => setAccessType('AUTOMATED')}
            className={`cursor-pointer border rounded-xl p-8 bg-white space-y-3 transition-all ${accessType === 'AUTOMATED' ? 'border-green-500 ring-1 ring-green-400' : 'border-gray-300'
              }`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                name="accessType"
                checked={accessType === 'AUTOMATED'}
                onChange={() => setAccessType('AUTOMATED')}
                className="mt-1 h-5 w-5 accent-green-600"
              />
              <div className="flex items-center justify-center h-[4rem] w-[4rem] rounded-xl bg-[#0D9B50]">
                <FaRobot size={20} color="white" />
              </div>
              <h3 className="text-[1.8rem] font-semibold">Automated Content Publishing</h3>
            </div>
            <div className="space-y-1 text-[1.4rem] text-gray-600">
              {AutomatedCheckList.map((item, index) => (
                <div key={index} className="flex items-center space-y-[0.8rem]">
                  <MdDone size={16} color="#0D9B50" className="ml-[1.8rem] mr-[1rem] mt-[0.4rem]" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Manual */}
          <div
            onClick={() => setAccessType('MANUAL')}
            className={`cursor-pointer border rounded-xl p-8 bg-white space-y-3 transition-all ${accessType === 'MANUAL' ? 'border-green-500 ring-1 ring-green-400' : 'border-gray-300'
              }`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                name="accessType"
                checked={accessType === 'MANUAL'}
                onChange={() => setAccessType('MANUAL')}
                className="mt-1 h-5 w-5 accent-green-600"
              />
              <div className="flex items-center justify-center h-[4rem] w-[4rem] rounded-xl bg-[#0D9B50]">
                <MdOutlineBackHand size={20} color="white" />
              </div>
              <h3 className="text-[1.8rem] font-semibold">Manual Upload</h3>
            </div>
            <div className="space-y-1 text-[1.4rem] text-gray-600">
              {ManualCheckList.map((item, index) => (
                <div key={index} className="flex items-center space-y-[1rem]">
                  <MdDone size={16} color="#0D9B50" className="ml-[1.8rem] mr-[1rem] mt-[0.6rem]" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Continue buttons */}
        <div className="mt-10 mb-[5rem] flex flex-col sm:flex-row gap-4">
          {(!platformLower || platformLower === 'youtube') && (
            <button
              onClick={handleContinue}
              className="w-[22rem] px-4 py-[1rem] text-[1.6rem] rounded-[1rem] bg-[#0D9B50] text-white hover:bg-[#0C8B48]/90 disabled:opacity-50"
            >
              Continue with YouTube
            </button>
          )}
          {(!platformLower || platformLower === 'instagram') && (
            <button
              onClick={handleInstagramConnect}
              className="w-[22rem] px-4 py-[1rem] text-[1.6rem] rounded-[1rem] border border-[#0D9B50] text-[#0D9B50] hover:bg-[#E9F7EF]"
            >
              Connect Instagram
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChooseAccessType;
